import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { api, applyMeta } from "@/lib/api";
import AgencyApp from "@/apps/AgencyApp";
import WorkspaceApp from "@/apps/WorkspaceApp";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import PublicForm from "@/pages/PublicForm";
import { PublicVideoReview } from "@/pages/ClientVideoView";
import PublicSocialReview from "@/pages/PublicSocialReview";
import CustomCodeInjector from "@/components/CustomCodeInjector";
import { ConfirmHost } from "@/lib/confirm";
import MagicLogin from "@/pages/MagicLogin";

function MetaManager() {
  useEffect(() => {
    api.get("/settings/public").then(({ data }) => applyMeta(data)).catch(() => {});
  }, []);
  return null;
}

// Radix Dialog/Sheet trap focus while open (FocusScope). When a third-party widget
// like Userback renders over a modal, that trap yanks focus back and blocks typing in
// its fields. This lets focus reach Userback by stopping the focus event before Radix's
// document-level listener runs (capture phase → halts the bubble-phase FocusScope handler).
function isUserbackNode(node) {
  let el = node;
  while (el) {
    if (el.nodeType === 1) {
      const id = (el.id || "").toLowerCase();
      const cls = (typeof el.className === "string" ? el.className : "").toLowerCase();
      if (id.includes("userback") || cls.includes("userback")) return true;
      if (el.tagName === "IFRAME") {
        const src = (el.getAttribute("src") || "").toLowerCase();
        const title = (el.getAttribute("title") || "").toLowerCase();
        if (src.includes("userback") || title.includes("userback")) return true;
      }
    }
    el = el.parentNode || (el.getRootNode && el.getRootNode() !== el && el.getRootNode().host);
  }
  return false;
}

function WidgetFocusGuard() {
  useEffect(() => {
    const guard = (e) => { if (isUserbackNode(e.target)) e.stopImmediatePropagation(); };
    document.addEventListener("focusin", guard, true);
    return () => document.removeEventListener("focusin", guard, true);
  }, []);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <MetaManager />
        <WidgetFocusGuard />
        <CustomCodeInjector />
        <Routes>
          <Route path="/" element={<Navigate to="/console" replace />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/magic-login" element={<MagicLogin />} />
          <Route path="/form/:token" element={<PublicForm />} />
          <Route path="/review/video/:token" element={<PublicVideoReview />} />
          <Route path="/social-review/:token" element={<PublicSocialReview />} />
          <Route path="/console/*" element={<AgencyApp />} />
          <Route path="/agency/*" element={<Navigate to={`/console${window.location.search}`} replace />} />
          <Route path="/:slug/*" element={<WorkspaceApp />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="light" position="top-right" />
      <ConfirmHost />
    </div>
  );
}

export default App;
